import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, EXPENSE_CATEGORIES } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { date: new Date().toISOString().slice(0, 10), category: "other", description: "", amount: 0, vendor: "", notes: "" };

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const r = await api.get("/expenses"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.description || Number(form.amount) <= 0) { toast.error("Description and amount required"); return; }
    await api.post("/expenses", { ...form, amount: Number(form.amount) });
    toast.success("Expense logged"); setOpen(false); setForm(EMPTY); load();
  };

  const remove = async (id) => { await api.delete(`/expenses/${id}`); load(); };

  const total = items.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader
        overline="Operating Costs"
        title="Expenses"
        description="Track dealership overhead beyond vehicle costs."
        action={<Button data-testid="add-expense" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus size={16} className="mr-2" />Add Expense</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Expenses</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtMoney(total)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Entries</div>
          <div className="font-display text-2xl font-bold mt-1">{items.length}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Avg / Entry</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtMoney(items.length ? total / items.length : 0)}</div>
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Date", "Category", "Description", "Vendor", "Amount", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No expenses logged.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3">{fmtDate(e.date || e.created_at)}</td>
                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs capitalize">{e.category.replace(/_/g, " ")}</span></td>
                <td className="px-4 py-3 font-medium">{e.description}</td>
                <td className="px-4 py-3">{e.vendor || "—"}</td>
                <td className="px-4 py-3 font-semibold text-red-600">{fmtMoney(e.amount)}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(e.id)} className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Log Expense</DialogTitle>
            <DialogDescription>Operating cost not tied to a specific vehicle.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Date</Label><Input data-testid="e-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="e-category" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Description</Label><Input data-testid="e-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Vendor</Label><Input data-testid="e-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Amount</Label><Input data-testid="e-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-lg" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="e-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg" /></div>
          </div>
          <DialogFooter><Button data-testid="e-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
